import React, { useImperativeHandle, useMemo, useRef } from 'react';
import { Box } from '@tanium/react-box';
import { LayoutStyleProps } from '@tanium/style-system';
import { useListOverflow } from '../../hooks/useListOverflow';
import { Id } from '../../types/Id';
import { ItemRenderer } from '../Item/ItemRenderer';
import { EmptyState } from './EmptyState';
import { ListProps } from './ListProps';
import { RibbonListHandles } from './RibbonListHandles';

/**
 * Static-specific props used by RibbonListStatic.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface RibbonListStaticProps<TId extends Id, TItem> {
  height?: LayoutStyleProps['height'];
  maxHeight?: LayoutStyleProps['maxHeight'];
}

interface Props<TId extends Id, TItem>
  extends ListProps<TId, TItem>,
    RibbonListStaticProps<TId, TItem> {}

const emptyStateJsx = (
  <Box height={8} position="relative">
    <EmptyState />
  </Box>
);

/**
 * A component that renders the main body of a StackedRibbonList statically to the DOM. This
 * component should be rendered inside a RibbonListWrapper.
 */
const RibbonListStaticInner = <TId extends Id, TItem>(
  { height, itemCount, itemIdGetter, maxHeight, onListOverflowChange }: Props<TId, TItem>,
  ref: React.Ref<RibbonListHandles>,
) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useListOverflow(outerRef, onListOverflowChange);

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      outerRef.current!.scrollTop = 0;
    },
  }));

  const children = useMemo(() => {
    if (itemCount === 0) {
      return emptyStateJsx;
    }

    const items = [];
    for (let i = 0; i < itemCount; i += 1) {
      items.push(<ItemRenderer key={itemIdGetter(i)} index={i} />);
    }

    return items;
  }, [itemCount, itemIdGetter]);

  return (
    <Box role="rowgroup" maxHeight={maxHeight} height={height} overflow="auto" ref={outerRef}>
      <div role="presentation" ref={innerRef}>
        {children}
      </div>
    </Box>
  );
};

export const RibbonListStatic = React.forwardRef(RibbonListStaticInner);
